/**
 * AI プロンプト管理ドメインモデル
 *
 * marketing-intelligence-hub のプロンプト管理と同構造で、カテゴリ (プロンプトタイプ) ×
 * 用途別プロンプト (バリアント) × 変数を構造化して Firestore (`creative_prompts`) で管理する。
 * 1 カテゴリに用途別のプロンプトを何個でも置け、複数を同時に有効化できる
 * (AI は説明文を見て用途に合うものを選ぶ)。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business AIレビュー・クリエイティブ作成プロンプトのカテゴリ×用途別バリアント×変数管理。複数同時有効化を許し、テンプレート変数展開とカテゴリ定義SSOTを提供。
 * @graph-connects none
 */
import { z } from 'zod';
import { AuditedTimestampsSchema } from './models/common.js';
/**
 * creative のプロンプトタイプスキーマ。
 * @graph-connects none
 */
export const CreativePromptTypeSchema = z.enum([
    'design_review',
    'creative_generation',
    'ai_handoff_behavior',
]);
/**
 * プロンプトカテゴリスキーマ (Web UI のセクション分け)。
 * @graph-connects none
 */
export const PromptCategorySchema = z.enum(['review', 'generation', 'behavior']);
/**
 * プロンプトテンプレート変数の定義スキーマ。
 * @graph-connects none
 */
export const PromptVariableSchema = z.object({
    name: z.string().min(1).max(40),
    description: z.string().min(1).max(200),
});
/**
 * プロンプトタイプ定義スキーマ (SSOT は {@link PROMPT_TYPE_CONFIGS})。
 * @graph-connects none
 */
export const PromptTypeConfigSchema = z.object({
    type: CreativePromptTypeSchema,
    name: z.string().min(1),
    description: z.string().min(1),
    category: PromptCategorySchema,
    variables: z.array(PromptVariableSchema),
    defaultSystemPrompt: z.string().min(1),
    defaultUserPrompt: z.string().min(1),
});
/**
 * プロンプトバリアントの作成・更新入力スキーマ。
 * @graph-connects none
 */
export const PromptVariantInputSchema = z.object({
    variantName: z.string().min(1).max(60),
    variantDescription: z.string().max(300).optional(),
    systemPrompt: z.string().min(1).max(20000),
    userPromptTemplate: z.string().min(1).max(20000),
});
/**
 * 永続化済みのプロンプトバリアントスキーマ。
 * ドキュメント ID は `{promptType}__{variantId}` ({@link makePromptDocId})。
 * @graph-connects none
 */
export const PromptVariantSchema = z
    .object({
    id: z.string().min(1),
    promptType: CreativePromptTypeSchema,
    variantName: z.string().min(1).max(60),
    variantDescription: z.string().max(300),
    isActive: z.boolean(),
    systemPrompt: z.string().min(1).max(20000),
    userPromptTemplate: z.string().min(1).max(20000),
})
    .extend(AuditedTimestampsSchema.shape);
/**
 * プロンプトタイプ定義の SSOT。
 *
 * 変数は `{{NAME}}` プレースホルダとしてテンプレートに埋め込み、
 * {@link renderPromptTemplate} が実データで展開する。
 * @graph-connects none
 */
export const PROMPT_TYPE_CONFIGS = {
    design_review: {
        type: 'design_review',
        name: 'デザインレビュー',
        description: 'バナー等のクリエイティブを Design Guide に照らしてレビューし、チェックリスト番号と Principle を引用した指摘を問いとして返す',
        category: 'review',
        variables: [
            { name: 'DESIGN_GUIDE', description: 'Design Guide 全文 (Markdown)' },
            { name: 'REVIEW_EXAMPLES', description: '過去のデザイナーレビューコメント (few-shot 例)' },
            { name: 'TARGET_BRAND', description: 'レビュー対象のブランド (aC / aCM / Mall 等)' },
        ],
        defaultSystemPrompt: [
            'あなたは airCloset のデザインレビュー補助者です。最終判断者ではありません。',
            '以下の Design Guide の基準のみに基づいてレビューし、チェックリストにない独自観点を追加しないでください。',
            '判定は必ず項目番号と Principle を引用し、指摘はすべて「問い」として返してください。',
            '',
            '{{DESIGN_GUIDE}}',
        ].join('\n'),
        defaultUserPrompt: [
            '添付のクリエイティブ ({{TARGET_BRAND}} 向け) をレビューしてください。',
            '',
            '過去のデザイナーレビュー例 (トーンと着眼点の参考):',
            '{{REVIEW_EXAMPLES}}',
        ].join('\n'),
    },
    creative_generation: {
        type: 'creative_generation',
        name: 'クリエイティブ作成',
        description: 'Design Guide のトークン・フォント・チェックリストに準拠したクリエイティブ (バナー案・コピー案等) を作成する',
        category: 'generation',
        variables: [
            { name: 'DESIGN_GUIDE', description: 'Design Guide 全文 (Markdown)' },
            { name: 'TARGET_BRAND', description: '作成対象のブランド (aC / aCM / Mall 等)' },
            { name: 'BRIEF', description: '作成依頼の内容 (目的・訴求・サイズ等)' },
        ],
        defaultSystemPrompt: [
            'あなたは airCloset のクリエイティブ制作補助者です。',
            '以下の Design Guide (カラートークン・フォント規定・チェックリスト) に必ず準拠して制作してください。',
            '規定フォントが使えない場合は代替せずエラーとして停止してください。',
            '',
            '{{DESIGN_GUIDE}}',
        ].join('\n'),
        defaultUserPrompt: [
            '{{TARGET_BRAND}} 向けのクリエイティブを作成してください。',
            '',
            '依頼内容:',
            '{{BRIEF}}',
        ].join('\n'),
    },
    ai_handoff_behavior: {
        type: 'ai_handoff_behavior',
        name: 'AI 振る舞い規約',
        description: 'レビュー・制作の前提となる AI の振る舞い規約 (補助者としての立ち位置・判定別の応答方法)',
        category: 'behavior',
        variables: [
            { name: 'JUDGMENT_POLICY', description: '判定ロジックと衝突解決ルール (Markdown)' },
            { name: 'CONTACTS', description: '相談先一覧' },
        ],
        defaultSystemPrompt: [
            'あなたはデザインレビューの補助者であり、最終判断者ではありません。',
            '指摘はすべて「問い」として返し、断定しないでください。判断はユーザーに委ねます。',
            '- NG → 指摘と修正方向を提示し「修正しますか？デザイナーに確認しますか？」と問う',
            '- 注意 → 指摘し「意図的ですか？」と問い「デザイナーに相談する？」を提案する',
            '- OK → 問題ない旨を伝える',
            '',
            '{{JUDGMENT_POLICY}}',
        ].join('\n'),
        defaultUserPrompt: ['相談先:', '{{CONTACTS}}'].join('\n'),
    },
};
/**
 * プロンプトタイプとバリアント ID から Firestore ドキュメント ID を生成する。
 * @param promptType プロンプトタイプ
 * @param variantId バリアント ID
 * @returns `{promptType}__{variantId}` 形式のドキュメント ID
 * @graph-connects none
 */
export function makePromptDocId(promptType, variantId) {
    return `${promptType}__${variantId}`;
}
/**
 * Firestore ドキュメント ID (`{promptType}__{variantId}`) から variantId を取り出す。
 * @param promptType プロンプトタイプ (カテゴリ)
 * @param docId ドキュメント ID
 * @returns variantId (接頭辞が一致しなければ docId をそのまま返す)
 * @graph-connects none
 */
export function parsePromptVariantId(promptType, docId) {
    const prefix = `${promptType}__`;
    return docId.startsWith(prefix) ? docId.slice(prefix.length) : docId;
}
/**
 * バリアント名から URL セーフなバリアント ID を生成する。
 *
 * 日本語 (ひらがな・カタカナ・漢字) は保持し、それ以外の記号を `_` に正規化する。
 * 空になった場合は fallback を返す (呼び出し側でタイムスタンプ等を渡す)。
 * @param name バリアント表示名
 * @param fallback 正規化後に空となった場合の代替 ID
 * @returns 最大 30 文字のバリアント ID
 * @graph-connects none
 */
export function generatePromptVariantId(name, fallback) {
    const normalized = name
        .toLowerCase()
        .replace(/[^\w぀-ゟ゠-ヿ一-龯]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .substring(0, 30);
    return normalized || fallback;
}
/**
 * `{{NAME}}` プレースホルダを変数値で展開する。
 *
 * 値が渡されなかったプレースホルダは原文のまま残し、`missingVariables` で報告する
 * (呼び出し側が「変数不足」をユーザー/AI に明示できるようにするため)。
 * @param template プロンプトテンプレート
 * @param variables 変数名 → 値のマップ
 * @returns 展開済みテンプレートと未解決変数名のリスト
 * @graph-connects none
 */
export function renderPromptTemplate(template, variables) {
    const missing = new Set();
    const rendered = template.replace(/\{\{([A-Z0-9_]+)\}\}/g, (placeholder, name) => {
        const value = variables[name];
        if (value === undefined) {
            missing.add(name);
            return placeholder;
        }
        return value;
    });
    return { rendered, missingVariables: [...missing] };
}
/**
 * プロンプトバリアントの system / user テンプレートをまとめて変数展開する。
 * @param variant 展開対象のバリアント
 * @param variables 変数名 → 値のマップ
 * @returns 展開済み system / user プロンプトと未解決変数名
 * @graph-connects none
 */
export function renderPromptVariant(variant, variables) {
    const system = renderPromptTemplate(variant.systemPrompt, variables);
    const user = renderPromptTemplate(variant.userPromptTemplate, variables);
    return {
        systemPrompt: system.rendered,
        userPrompt: user.rendered,
        missingVariables: [...new Set([...system.missingVariables, ...user.missingVariables])],
    };
}
//# sourceMappingURL=prompts.js.map